import React, { useEffect, useContext, Suspense } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { FacebookProvider } from 'react-facebook';
import SimpleReactLightboxProvider from 'simple-react-lightbox';

import { useIntl } from 'react-intl';

import { AppSettingsContext } from 'AppSettingsContext';
import { IntlContext } from 'IntlContext';
import { ThemeContext, useTheme } from 'ThemeContext';

import LoadingScreen from 'components/LoadingScreen';
import ErrorScreen from 'components/ErrorScreen';
import LoadingData from 'components/LoadingData';
import Toolbar from 'components/Toolbar';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Cookies from 'components/Cookies';
import Popup from 'components/Popup';

import { Public as PublicRoutes } from 'routes';
import { withRouter } from 'react-router-dom';

import { GlobalStyles } from 'assets/Global.style';

import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const GET_PORTAL = gql`
  query GET_PORTAL {
    getPortal {
      name
      logo_url
      mourning
      theme {
        theme_id
        name
      }
      theme_mode
      theme_color {
        theme_color_id
        value
      }
      menu
      footerMenu
      copyright {
        owner
        period
        producer
        producerHref
      }
      facebook_api_key
      banner_options {
        rotation_banner_first
        rotation_banner
        show_banner_thumbnails
      }
      headline_photomode_id
    }
  }
`;
const GET_WIDGET = gql`
  query GET_WIDGET($area: Int!) {
    getWidgetArea(widget_area_id: $area) {
      widgets {
        widget_id
        data
      }
    }
  }
`;

toast.configure({
  autoClose: 2000,
  draggable: false,
  hideProgressBar: true
});

function WaitingComponent(Component) {
  return props => (
    <Suspense fallback={<LoadingData fullmode={true} />}>
      <Component {...props} />
    </Suspense>
  );
}

const System = ({ location }) => {
  const intl = useIntl();
  const { loading, error, data } = useQuery(GET_PORTAL);
  const { data: widgetTopLeftBarData } = useQuery(GET_WIDGET, {
    variables: {
      area: 1
    }
  });
  const { data: widgetTopRightBarData } = useQuery(GET_WIDGET, {
    variables: {
      area: 28
    }
  });
  const { data: widgetMenuBarData } = useQuery(GET_WIDGET, {
    variables: {
      area: 2
    }
  });
  const { data: widgetMenuLogoBarData } = useQuery(GET_WIDGET, {
    variables: {
      area: 29
    },
    skip: !!data && data.logo_url
  });
  const { data: widgetPopupData } = useQuery(GET_WIDGET, {
    variables: {
      area: 7
    }
  });
  const { data: widgetFooterColumn1Data } = useQuery(GET_WIDGET, {
    variables: {
      area: 3
    }
  });
  const { data: widgetFooterColumn2Data } = useQuery(GET_WIDGET, {
    variables: {
      area: 4
    }
  });
  const { data: widgetFooterColumn3Data } = useQuery(GET_WIDGET, {
    variables: {
      area: 5
    }
  });
  const { data: widgetFooterColumn4Data } = useQuery(GET_WIDGET, {
    variables: {
      area: 6
    }
  });

  const [appSettings, appSettingsDispatch] = useContext(AppSettingsContext);
  const { switchLanguage } = useContext(IntlContext);
  const { theme: appTheme, switchTheme } = useContext(ThemeContext);
  const theme = useTheme();

  useEffect(() => {
    let timeoutTimer;
    function handleResize() {
      if (timeoutTimer) clearTimeout(timeoutTimer);
      timeoutTimer = setTimeout(() => {
        if (window.FB && window.FB.XFBML) window.FB.XFBML.parse();
      }, 200);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      if (timeoutTimer) clearTimeout(timeoutTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (loading) {
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_PENDING'
      });
    } else if (error) {
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_REJECTED'
      });
    } else if (data) {
      const { getPortal } = data;
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_FULFILLED',
        payload: {
          name: getPortal.name,
          logo: getPortal.logo_url,
          mourning: getPortal.mourning,
          theme: getPortal.theme && getPortal.theme.theme_id,
          themeColors: getPortal.theme_color,
          orientation: getPortal.theme_mode === 2 ? 'invert' : '',
          menu: getPortal.menu,
          footerMenu: getPortal.footerMenu,
          cookies: {
            text:
              'Ta strona korzysta z plików cookie, kontynuując przeglądanie witryny, wyrażasz zgodę na korzystanie z plików cookie.',
            slug: '/polityka-cookies'
          },
          copyright: {
            period: getPortal.copyright && getPortal.copyright.period,
            owner: getPortal.copyright && getPortal.copyright.owner,
            producer: getPortal.copyright && getPortal.copyright.producer,
            producerHref:
              getPortal.copyright && getPortal.copyright.producerHref
          },
          facebook_api_key: getPortal.facebook_api_key,
          banner_options: {
            rotation_banner_first:
              getPortal.banner_options &&
              getPortal.banner_options.rotation_banner_first,
            rotation_banner:
              getPortal.banner_options &&
              getPortal.banner_options.rotation_banner,
            show_banner_thumbnails:
              getPortal.banner_options &&
              getPortal.banner_options.show_banner_thumbnails
          }
        }
      });
    } else {
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_REJECTED'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, data]);

  useEffect(() => {
    if (widgetTopLeftBarData) {
      const {
        getWidgetArea: { widgets }
      } = widgetTopLeftBarData;
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_SOCIALS_FULFILLED',
        payload: {
          socials: widgets
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetTopLeftBarData]);

  useEffect(() => {
    if (widgetTopRightBarData) {
      const {
        getWidgetArea: { widgets }
      } = widgetTopRightBarData;
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_SETTINGS_FULFILLED',
        payload: {
          settings: widgets
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetTopRightBarData]);

  useEffect(() => {
    if (widgetMenuBarData) {
      const {
        getWidgetArea: { widgets }
      } = widgetMenuBarData;
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_NAV_MENU_FULFILLED',
        payload: {
          navMenu: widgets
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetMenuBarData]);

  useEffect(() => {
    if (widgetMenuLogoBarData) {
      const {
        getWidgetArea: { widgets }
      } = widgetMenuLogoBarData;

      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_NAV_MENU_LEFT_FULFILLED',
        payload: {
          navMenuLeft: widgets
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetMenuLogoBarData]);

  useEffect(() => {
    if (widgetPopupData) {
      const {
        getWidgetArea: { widgets }
      } = widgetPopupData;
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_POPUP_FULFILLED',
        payload: {
          popup: widgets
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetPopupData]);

  useEffect(() => {
    if (widgetFooterColumn1Data) {
      const {
        getWidgetArea: { widgets }
      } = widgetFooterColumn1Data;
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_FOOTER_COLUMN_FULFILLED',
        payload: {
          column: 0,
          footerColumn: widgets
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetFooterColumn1Data]);

  useEffect(() => {
    if (widgetFooterColumn2Data) {
      const {
        getWidgetArea: { widgets }
      } = widgetFooterColumn2Data;
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_FOOTER_COLUMN_FULFILLED',
        payload: {
          column: 1,
          footerColumn: widgets
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetFooterColumn2Data]);

  useEffect(() => {
    if (widgetFooterColumn3Data) {
      const {
        getWidgetArea: { widgets }
      } = widgetFooterColumn3Data;
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_FOOTER_COLUMN_FULFILLED',
        payload: {
          column: 2,
          footerColumn: widgets
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetFooterColumn3Data]);

  useEffect(() => {
    if (widgetFooterColumn4Data) {
      const {
        getWidgetArea: { widgets }
      } = widgetFooterColumn4Data;
      appSettingsDispatch({
        type: 'SET_APP_SETTINGS_FOOTER_COLUMN_FULFILLED',
        payload: {
          column: 3,
          footerColumn: widgets
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetFooterColumn4Data]);

  useEffect(() => {
    if (appSettings.lang && appSettings.lang !== intl.locale)
      switchLanguage(appSettings.lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSettings.lang]);

  useEffect(() => {
    if (appSettings.theme && appSettings.theme !== appTheme)
      switchTheme(appSettings.theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appSettings.theme]);

  useEffect(() => {
    const hashId = location.hash.replace('#', '');
    if (hashId) {
      let timePassed = 0;

      const interval = setInterval(() => {
        const element = document.getElementById(hashId);
        timePassed += 100;
        if (element) {
          element.scrollIntoView();
          clearInterval(interval);
        }
        if (timePassed >= 5000) {
          clearInterval(interval);
        }
      }, 250);

      return () => {
        clearInterval(interval);
      };
    }
    //  eslint-disable-next-line
  }, []);

  if (
    appSettings &&
    appSettings.isLoaded &&
    !appSettings.isError &&
    appSettings.theme === appTheme &&
    appSettings.lang === intl.locale
  ) {
    return (
      <>
        <FacebookProvider
          appId={appSettings.facebook_api_key || 'NONE'}
          language={appSettings.lang}
        >
          <SimpleReactLightboxProvider>
            <GlobalStyles
              theme={theme.global}
              highContrast={appSettings.highContrast}
              mourning={appSettings.mourning}
              scale={appSettings.fontSizeScale}
            />
            <Toolbar
              widgets={{
                left: appSettings.socials,
                right: appSettings.settings
              }}
            />
            <Header
              name={appSettings.name}
              logo={appSettings.logo}
              mourning={appSettings.mourning}
              navMenu={appSettings.navMenu}
              navMenuLeft={appSettings.navMenuLeft}
              menu={appSettings.menu}
            />
            <Switch>
              {PublicRoutes.map(({ path, exact, name, component }) => {
                return (
                  <Route key={name} path={path} exact={exact} name={name}>
                    {WaitingComponent(component)}
                  </Route>
                );
              })}
              <Redirect to={'/404'} />
            </Switch>
            <Footer
              logo={appSettings.logo}
              mourning={appSettings.mourning}
              menu={appSettings.footerMenu}
              copyright={appSettings.copyright}
              widgets={appSettings.footer}
              // handleNewsletters={handleNewsletters}
              // handleSearch={handleSearch}
            />
            <Cookies
              text={appSettings.cookies.text}
              slug={appSettings.cookies.slug}
            />
            {appSettings.popup && appSettings.popup.length > 0 ? (
              <Popup data={appSettings.popup} />
            ) : (
              ''
            )}
          </SimpleReactLightboxProvider>
        </FacebookProvider>
      </>
    );
  } else if (appSettings && appSettings.isLoaded && appSettings.isError) {
    return (
      <>
        <GlobalStyles
          theme={theme.global}
          fullmode={true}
          highContrast={appSettings.highContrast}
          mourning={appSettings.mourning}
          scale={appSettings.fontSizeScale}
        />
        <ErrorScreen />
      </>
    );
  } else {
    return (
      <>
        <GlobalStyles
          theme={theme.global}
          fullmode={true}
          highContrast={appSettings.highContrast}
          mourning={appSettings.mourning}
          scale={appSettings.fontSizeScale}
        />
        <LoadingScreen />
      </>
    );
  }
};

export default withRouter(System);
